import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import AuthNavbar from "./AuthNavbar";
import { requestEmailSignup } from "../../services/auth";
import {
  validateEmail,
  isTemporaryEmail,
  isFreeEmail,
  hasEmailPlusAddressing,
} from "../../utils/validation";
import "./Auth.css";
import { useTheme } from "../../contexts/ThemeContext";

declare global {
  interface Window {
    turnstile: any;
    onloadTurnstileCallback?: () => void;
  }
}

// Environment check
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000XX";

enum SignupStep {
  FORM,
  EMAIL_VERIFICATION_SENT,
}

interface EmailSignupProps {
  onSignupSuccess?: () => void;
}

const EmailSignup: React.FC<EmailSignupProps> = ({ onSignupSuccess }) => {
  const { theme } = useTheme();
  
  // Form states
  const [step, setStep] = useState<SignupStep>(SignupStep.FORM);
  const [fullname, setFullname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  
  // UI states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Validation states
  const [emailValidationMsg, setEmailValidationMsg] = useState<string | null>(null);
  const [fullnameValidationMsg, setFullnameValidationMsg] = useState<string | null>(null);
  const [companyValidationMsg, setCompanyValidationMsg] = useState<string | null>(null);
  
  // Turnstile (captcha) references
  const turnstileRef = useRef<any>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileScriptLoaded = useRef<boolean>(false);

  // Load Turnstile script
  useEffect(() => {
    // Skip loading Turnstile in development
    if (IS_DEVELOPMENT || step !== SignupStep.FORM || turnstileScriptLoaded.current) return;

    // Mark script as loaded to prevent duplicate loading
    turnstileScriptLoaded.current = true;

    // Create the script element
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;
    script.id = "turnstile-script";

    // Set up the callback
    window.onloadTurnstileCallback = () => {
      initializeTurnstile();
    };

    // Add script to body
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      if (document.getElementById("turnstile-script")) {
        document.body.removeChild(script);
      }
      // Clean up the callback when unmounting
      if (window.onloadTurnstileCallback) {
        delete window.onloadTurnstileCallback;
      }
      // Remove widget if it exists
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, [step]);

  // Initialize Turnstile widget
  const initializeTurnstile = useCallback(() => {
    if (turnstileContainer.current && window.turnstile) {
      // If a widget already exists, remove it first
      if (turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
      }

      // Create new widget
      turnstileWidgetId.current = window.turnstile.render(
        turnstileContainer.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: theme === "light" ? "light" : "dark",
          callback: (token: string) => {
            turnstileRef.current = token;
          },
          "error-callback": () => {
            turnstileRef.current = null;
          },
        }
      );
    }
  }, [theme]);

  // Effect to handle theme changes
  useEffect(() => {
    if (!IS_DEVELOPMENT && turnstileScriptLoaded.current && window.turnstile) {
      initializeTurnstile();
    }
  }, [theme, initializeTurnstile]);

  // Reset Turnstile when coming back to the form step
  useEffect(() => {
    if (
      !IS_DEVELOPMENT &&
      turnstileWidgetId.current &&
      window.turnstile &&
      step === SignupStep.FORM
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.warn("Failed to reset Turnstile widget:", error);
        // Re-initialize the widget if reset fails
        setTimeout(() => {
          initializeTurnstile();
        }, 100);
      }
    }
  }, [step, initializeTurnstile]);



  // Validate form fields
  const validateForm = (): boolean => {
    if (!fullname.trim()) {
      setError("Please enter your full name");
      return false;
    }

    if (!email.trim() || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (isTemporaryEmail(email)) {
      setError("Temporary or disposable email addresses are not allowed");
      return false;
    }

    if (isFreeEmail(email)) {
      setError("Personal email addresses are not allowed. Please use a work email");
      return false;
    }

    if (hasEmailPlusAddressing(email)) {
      setError("Email addresses with '+' are not allowed");
      return false;
    }

    if (!company.trim()) {
      setError("Please enter your company name");
      return false;
    }

    return true;
  };

  // Validate email on blur
  const validateEmailOnBlur = () => {
    setEmailValidationMsg(null);

    if (!email.trim()) {
      setEmailValidationMsg("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailValidationMsg("Please enter a valid email address");
      return;
    }

    if (isTemporaryEmail(email)) {
      setEmailValidationMsg("Temporary or disposable email addresses are not allowed");
      return;
    }

    if (isFreeEmail(email)) {
      setEmailValidationMsg("Personal email addresses are not allowed. Please use a work email");
      return;
    }

    if (hasEmailPlusAddressing(email)) {
      setEmailValidationMsg("Email addresses with '+' are not allowed");
      return;
    }
  };

  // Validate fullname on blur
  const validateFullnameOnBlur = () => {
    setFullnameValidationMsg(null);

    if (!fullname.trim()) {
      setFullnameValidationMsg("Full name is required");
    }
  };

  // Validate company on blur
  const validateCompanyOnBlur = () => {
    setCompanyValidationMsg(null);

    if (!company.trim()) {
      setCompanyValidationMsg("Company/Organization is required");
    }
  };

  // Handle form submission
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Get Turnstile token - skip verification in development mode
      const turnstileToken = IS_DEVELOPMENT
        ? "dev_verification_token"
        : turnstileRef.current;

      if (!IS_DEVELOPMENT && !turnstileToken) {
        setError("Please complete the security check");
        setLoading(false);
        return;
      }

      // Submit signup request
      const result = await requestEmailSignup({
        fullname: fullname.trim(),
        email: email.trim(),
        company: company.trim(),
        turnstileToken,
      });

      if (result.success) {
        setSuccess(result.message);
        setStep(SignupStep.EMAIL_VERIFICATION_SENT);
      } else {
        setError(result.error || "Failed to create account. Please try again.");
        // Reset the Turnstile widget
        if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget
      if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await requestEmailSignup({
        fullname: fullname.trim(),
        email: email.trim(),
        company: company.trim(),
      });

      if (result.success) {
        setSuccess("Verification email sent again");
      } else {
        setError(result.error || "Failed to resend verification email. Please try again.");
      }
    } catch (err) {
      setError("Failed to resend verification email. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Render signup form
  const renderSignupForm = () => {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-8 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex flex-col items-center mb-6">
              <motion.h2
                className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Create Account
              </motion.h2>
              <motion.p
                className="text-sm text-[color:var(--muted-foreground)] mt-1 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Enter your details to create your Voice Agent Portal account
              </motion.p>
            </div>

            <motion.form
              onSubmit={handleSignupSubmit}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <label
                  htmlFor="fullname"
                  className="block text-sm font-medium text-[color:var(--foreground)]"
                >
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullname"
                  value={fullname}
                  onChange={(e) => setFullname(e.target.value)}
                  onBlur={validateFullnameOnBlur}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  disabled={loading}
                  className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                />
                {fullnameValidationMsg && (
                  <motion.p
                    className="mt-1 text-sm text-red-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {fullnameValidationMsg}
                  </motion.p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-[color:var(--foreground)]"
                >
                  Work Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={validateEmailOnBlur}
                  placeholder="Enter your work email"
                  autoComplete="email"
                  disabled={loading}
                  className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                />
                {emailValidationMsg && (
                  <motion.p
                    className="mt-1 text-sm text-red-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {emailValidationMsg}
                  </motion.p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <label
                  htmlFor="company"
                  className="block text-sm font-medium text-[color:var(--foreground)]"
                >
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  onBlur={validateCompanyOnBlur}
                  placeholder="Enter your company name"
                  autoComplete="organization"
                  disabled={loading}
                  className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
                />
                {companyValidationMsg && (
                  <motion.p
                    className="mt-1 text-sm text-red-600"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {companyValidationMsg}
                  </motion.p>
                )}
              </motion.div>

              {/* Turnstile Widget - Only show in production */}
              {!IS_DEVELOPMENT ? (
                <motion.div
                  className="flex justify-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <div ref={turnstileContainer}></div>
                </motion.div>
              ) : (
                <motion.div
                  className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  Development mode: Turnstile verification bypassed
                </motion.div>
              )}

              {error && (
                <motion.div
                  className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              {success && (
                <motion.div
                  className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                  transition={{
                    duration: 0.4,
                    scale: { duration: 0.5, times: [0, 0.5, 1] },
                  }}
                >
                  <div className="flex items-center">
                    <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <motion.svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-600"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      >
                        <path d="M20 6L9 17l-5-5"></path>
                      </motion.svg>
                    </div>
                    <span className="text-green-600 font-medium">{success}</span>
                  </div>
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.9 }}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </motion.button>
            </motion.form>

            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.0 }}
            >
              <Link
                to="/login"
                className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
              >
                Already have an account? Login here
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  };

  // Render email verification sent step
  const renderEmailVerificationSent = () => {
    return (
      <>
        <AuthNavbar />
        <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
          <motion.div
            className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="flex flex-col items-center mb-6">
              <motion.div
                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </motion.svg>
              </motion.div>

              <motion.h2
                className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Check Your Email
              </motion.h2>

              <motion.p
                className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                We've sent a verification link to <strong>{email}</strong>
              </motion.p>
            </div>

            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Next steps:</strong>
                </p>
                <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the verification link in the email</li>
                  <li>Complete phone verification to access your dashboard</li>
                </ol>
              </div>

              {error && (
                <motion.div
                  className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              {success && (
                <motion.div
                  className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center">
                    <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-green-600"
                      >
                        <path d="M20 6L9 17l-5-5"></path>
                      </svg>
                    </div>
                    <span className="text-green-600 font-medium">{success}</span>
                  </div>
                </motion.div>
              )}

              <motion.button
                onClick={handleResendVerification}
                disabled={loading}
                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                {loading ? "Sending..." : "Resend Verification Email"}
              </motion.button>

              <motion.div
                className="text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <button
                  onClick={() => setStep(SignupStep.FORM)}
                  className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                >
                  ← Back to signup form
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </>
    );
  };

  // Render appropriate step
  const renderStep = () => {
    switch (step) {
      case SignupStep.FORM:
        return renderSignupForm();
      case SignupStep.EMAIL_VERIFICATION_SENT:
        return renderEmailVerificationSent();
      default:
        return renderSignupForm();
    }
  };

  return renderStep();
};

export default EmailSignup;